import React, { useState } from 'react';
import { Calendar, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface GoogleCalendarConnectProps {
  isConnected: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  isLoading?: boolean;
}

export const GoogleCalendarConnect: React.FC<GoogleCalendarConnectProps> = ({
  isConnected,
  onConnect,
  onDisconnect,
  isLoading = false,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      // Get Google OAuth URL
      const response = await fetch('/api/v1/calendar/auth-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: Math.random().toString(36).substring(7) }),
      });

      if (!response.ok) {
        throw new Error('Failed to get authorization URL');
      }

      const data = await response.json();
      
      // Open Google OAuth in popup
      const popup = window.open(
        data.auth_url,
        'google-calendar-auth',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      // Listen for popup close
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setIsConnecting(false);
          onConnect();
        }
      }, 1000);

    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await fetch('/api/v1/calendar/disconnect', {
        method: 'POST',
      });
      onDisconnect();
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Google Calendar Integration</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="h-5 w-5" />
        <h3 className="text-lg font-semibold">Google Calendar Integration</h3>
      </div>
      
      <p className="text-gray-600 mb-6">
        {isConnected 
          ? "Your Google Calendar is connected. Appointments will be automatically synced."
          : "Connect your Google Calendar to automatically sync appointments."
        }
      </p>

      {isConnected ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Connected to Google Calendar</span>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              • New appointments will be added to your calendar
            </p>
            <p className="text-sm text-gray-600">
              • Calendar conflicts will be automatically detected
            </p>
            <p className="text-sm text-gray-600">
              • Clients will receive calendar invitations
            </p>
          </div>
          <button 
            onClick={handleDisconnect}
            className="w-full px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Disconnect Google Calendar
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-orange-600">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Not Connected</span>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              • Connect to enable automatic appointment syncing
            </p>
            <p className="text-sm text-gray-600">
              • Avoid double bookings with conflict detection
            </p>
            <p className="text-sm text-gray-600">
              • Send automatic invitations to clients
            </p>
          </div>
          <button 
            onClick={handleConnect}
            disabled={isConnecting}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isConnecting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 inline animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Calendar className="h-4 w-4 mr-2 inline" />
                Connect Google Calendar
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
